import React from 'react';
import { Facebook, Instagram, Youtube, Phone, Mail, MapPin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-black text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Column */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_98529d53-1c35-46ad-aba2-5200ea95c0d0/artifacts/7g164avx_WhatsApp%20Image%202026-02-04%20at%2019.03.41.jpeg" 
                alt="ZOOM Academy" 
                className="h-10 w-auto"
              />
              <div>
                <h3 className="text-xl font-bold text-white">ZOOM ACADEMY</h3>
              </div>
            </div>
            <p className="text-gray-400 mb-4 text-sm leading-relaxed">
              Leading coaching institute in Hisar for Agriculture, Nursing, Fisheries, and Government exam preparation with proven results.
            </p>
            <p className="text-yellow-400 font-semibold italic text-sm">
              "हम नहीं... हमारी रैंक बोलती है"
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
                  Home
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
                  About Us
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('courses')} className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
                  Our Courses
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('results')} className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
                  Results
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('scholarship')} className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
                  Scholarship
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
                  Contact Us
                </button>
              </li>
            </ul>
          </div>

          {/* Courses */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Our Courses</h4>
            <ul className="space-y-2">
              <li className="text-gray-400 text-sm">B.Sc Agriculture (HAU)</li>
              <li className="text-gray-400 text-sm">B.Sc Fisheries</li>
              <li className="text-gray-400 text-sm">B.Sc Nursing</li>
              <li className="text-gray-400 text-sm">VLDA (LUVAS)</li>
              <li className="text-gray-400 text-sm">11th & 12th (CUET)</li>
              <li className="text-gray-400 text-sm">Government Exams</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-white">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  Gali No. 2, Rishi Nagar, Near Bus Stand, Hisar - 125001
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <div>
                  <a href="tel:9991129789" className="text-gray-400 hover:text-yellow-400 transition-colors text-sm block">
                    99911-29789
                  </a>
                  <a href="tel:8607029789" className="text-gray-400 hover:text-yellow-400 transition-colors text-sm block">
                    86070-29789
                  </a>
                </div>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <a href="mailto:info@zoomacademy.com" className="text-gray-400 hover:text-yellow-400 transition-colors text-sm">
                  info@zoomacademy.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Social Media Section */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} ZOOM ACADEMY. All rights reserved.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center space-x-4">
              <span className="text-gray-400 text-sm mr-2">Follow Us:</span>
              <a
                href="https://www.facebook.com/ZoomAcademyHisar"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/zoom_academy_hisar"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/@ZoomAcademyHisar"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-red-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-black border-t border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-gray-500 text-xs">
            Designed & Developed with ❤️ for ZOOM ACADEMY
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
